/**
 * Avoid adding @data types here.
 * Types that are data specific should not live in ui-core-crossplatform
 */
import { defineMessages } from 'react-intl'
import { Dimensions, Platform } from 'react-native'

import { IS_PRODUCTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { PROVIDER_LYRA_TYPES, ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

export enum BiometricsTypes {
  FaceID = 'Face ID',
  TouchID = 'Touch ID',
  FaceUnlock = 'Face Unlock',
  Fingerprint = 'Fingerprint',
}

export const BiometricsMessages = defineMessages({
  [BiometricsTypes.FaceID]: {
    defaultMessage: 'Face ID',
    description: 'Type of iOS mobile device login method using the face of the user',
  },
  [BiometricsTypes.TouchID]: {
    defaultMessage: 'Touch ID',
    description: 'Type of iOS mobile device login method using the fingerprint of the user',
  },
  [BiometricsTypes.FaceUnlock]: {
    defaultMessage: 'Face Unlock',
    description: 'Type of Android mobile device login method using the face of the user',
  },
  [BiometricsTypes.Fingerprint]: {
    defaultMessage: 'Fingerprint',
    description: 'Type of Android mobile device login method using the fingerprint of the user',
  },
})

const width = Dimensions.get('window').width
const height = Dimensions.get('window').height

export const Layout = {
  window: {
    width,
    height,
  },
  isSmallDevice: width <= 375,
}
export const IS_WEB = Platform.OS === 'web'
export const IS_LIBRARY = IS_WEB && !!window.WEB_APP
export const IS_CYPRESS = window.Cypress
export const IS_CYPRESS_MOBILE = window.Cypress && window.Cypress.env('app') === 'native-mobile'

export type BreakpointKeys =
  | 'mobileXxs'
  | 'mobileXs'
  | 'mobileXsHeight'
  | 'mobileS'
  | 'mobileM'
  | 'mobileL'
  | 'tablet'
  | 'laptop'
  | 'desktop'
  | 'desktopL'

export type Breakpoints = {
  [key in BreakpointKeys]: number
}

export const breakpoints: Breakpoints = {
  mobileXxs: 320,
  mobileXs: 376,
  mobileXsHeight: 668,
  mobileS: 460,
  mobileM: 575,
  mobileL: 640,
  tablet: 768,
  laptop: 992,
  desktop: 1200,
  desktopL: 1440,
}

export const STRIPE_API_VERSION = '2024-04-10'
export const STRIPE_TEST_KEY =
  'pk_test_519d6IIHQI3CHrUndaClcxS1vJ1Lkr0sU6pWcO8xa0BMNhYOWEHzytetOJ9olAeXWdLyfG2f8oW4NcH3GQMqBmc8J00sGdPB5vZ'

// React Native expects accessbilityRole to be from the
// following list of valid values, otherwise the app will error and crash
// https://reactnative.dev/docs/accessibility#accessibilityrole
export enum AccessibilityRolesNative {
  ADJUSTABLE = 'adjustable',
  ALERT = 'alert',
  BUTTON = 'button',
  CHECKBOX = 'checkbox',
  COMBOBOX = 'combobox',
  GRID = 'grid',
  HEADER = 'header',
  IMAGE = 'image',
  IMAGE_BUTTON = 'imagebutton',
  KEYBOARD_KEY = 'keyboardkey',
  LINK = 'link',
  MENU = 'menu',
  MENU_BAR = 'menubar',
  MENU_ITEM = 'menuitem',
  NONE = 'none',
  PROGRESS_BAR = 'progressbar',
  RADIO = 'radio',
  RADIO_GROUP = 'radiogroup',
  SCROLL_BAR = 'scrollbar',
  SEARCH = 'search',
  SPIN_BUTTON = 'spinbutton',
  SUMMARY = 'summary',
  SWITCH = 'switch',
  TAB = 'tab',
  TAB_LIST = 'tablist',
  TEXT = 'text',
  TIMER = 'timer',
  TOGGLE_BUTTON = 'togglebutton',
  TOOLBAR = 'toolbar',
}

export const NEXT_VIDEO_COUNTDOWN_SECS = 5

export const LYRA_THEME_STORAGE_KEY = 'lyraTheme'
export const LYRA_AURORA_THEME_STORAGE_KEY = 'lyraAuroraTheme'

export const ASSETS_CDN_URL = IS_PRODUCTION
  ? 'https://assets.production.lyrahealth.com/lyraweb/assets/'
  : 'https://assets.staging.lyrahealth.com/lyraweb/assets/'
export const TREATMENT_REC_PRIMARY_CARD_CDN_PREFIX = 'treatmentRecPrimaryCard-'
export const AURORA_TREATMENT_REC_CDN_PREFIX = 'treatmentRecs/treatmentRecPrimaryCard-'

const AI_COACH_CDN_URL = IS_PRODUCTION
  ? 'https://assets.production.lyrahealth.com/lyraweb/chat/ai_coach_icon.png'
  : 'https://assets.staging.lyrahealth.com/lyraweb/chat/ai_coach_icon.png'

export const aiCoachProviderInfo = {
  first_name: 'Lyra',
  last_name: 'AI',
  display_name: 'Lyra AI',
  is_blocked: false,
  treatments: ['Coaching'],
  lyra_id: '',
  lyra_type: PROVIDER_LYRA_TYPES.AI_COACH,
  lyraProviderImage: {
    imageUrl: AI_COACH_CDN_URL,
    rectangularImageUrl: AI_COACH_CDN_URL,
    portraitImageUrl: AI_COACH_CDN_URL,
  },
} as ProviderInfo
